import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import * as yup from 'yup';
import { Dealer, PreferenceType } from '../dealer.types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDealerMapContext } from '../context/dealer-map-context';
import { BackendRoutes } from '../dealer.const';
import { useConnectedUserInfo } from './use-connected-user-info.hook';
import { getDealerHttpService } from '../../../domain/http/http.factory';

const useDealerPreferences = () => {
  const { t } = useTranslation(['shared']);
  const queryClient = useQueryClient();
  const {
    queryKeys,
    setSelectedDealer,
    removeMainDealerModalState,
    setRemoveMainDealerModalState,
    mainDealerToDelete,
    setMainDealerToDelete,
    setPresentConnectedUserInfoModal,
    doNotShowConnectedUserInfoModalAgain,
    connectedUserInfoModalHasBeenDisplayed,
  } = useDealerMapContext();
  const { connectedUserInfo } = useConnectedUserInfo({ doNotShowConnectedUserInfoModalAgain });

  /*********************************************
   *                 DATA-FETCHING
   *********************************************/

  const updateDealerPreference = (
    oldData: Dealer[],
    variables: {
      dealerId: string;
      preferenceType: 'FAVORITE' | 'MAIN';
    }
  ) => {
    return oldData?.map((dealer: Dealer) => {
      if (dealer.id === variables.dealerId) {
        return { ...dealer, preference: { type: variables.preferenceType } };
      } else if (variables.preferenceType === 'MAIN' && dealer.preference?.type === 'MAIN') {
        return { ...dealer, preference: { ...dealer.preference, type: PreferenceType.FAVORITE } };
      }
      return dealer;
    });
  };

  const addDealerAsFavoriteOrMainMutation = useMutation(
    queryKeys?.get('FAVORITES') || ['DEALERS', 'FAVORITES'],
    ({ dealerId, preferenceType }: { dealerId: string; preferenceType: 'FAVORITE' | 'MAIN' }) => {
      return getDealerHttpService().put(BackendRoutes.DEALER_PREFERENCES__DEALER_ID.replace(':dealerId', dealerId), {
        type: preferenceType,
      });
    },
    {
      onSuccess(_, variables) {
        queryClient.setQueryData(queryKeys?.get('FAVORITES') || ['DEALERS', 'FAVORITES'], (oldData: Dealer[]) => {
          if (
            !connectedUserInfoModalHasBeenDisplayed?.current &&
            connectedUserInfo.isSuccess &&
            oldData?.length === 0 &&
            !doNotShowConnectedUserInfoModalAgain
          ) {
            setPresentConnectedUserInfoModal?.(true);
          }
          return updateDealerPreference(oldData, variables);
        });

        queryClient.setQueryData(queryKeys?.get('LOCATION') || ['DEALERS', 'LOCATION'], (oldData: Dealer[]) => {
          return updateDealerPreference(oldData, variables);
        });

        setRemoveMainDealerModalState?.('HIDDEN');
      },
    }
  );

  const removeDealerPreferenceMutation = useMutation(
    queryKeys?.get('FAVORITES') || ['DEALERS', 'FAVORITES'],
    ({ dealerId }: { dealerId: string }) => {
      return getDealerHttpService().delete(BackendRoutes.DEALER_PREFERENCES__DEALER_ID.replace(':dealerId', dealerId));
    },
    {
      onSuccess(_, variables) {
        queryClient.setQueryData(queryKeys?.get('FAVORITES') || ['DEALERS', 'FAVORITES'], (oldData: Dealer[]) => {
          return oldData?.filter((dealer: Dealer) => dealer.id !== variables.dealerId);
        });

        queryClient.setQueryData(queryKeys?.get('LOCATION') || ['DEALERS', 'LOCATION'], (oldData: Dealer[]) => {
          return oldData?.map((dealer: Dealer) => {
            if (dealer.id === variables.dealerId) {
              return { ...dealer, preference: null };
            }
            return dealer;
          });
        });
      },
    }
  );

  /*********************************************
   *                 FORM HANDLING
   *********************************************/

  const setNewMainDealerValidationSchema = useMemo(() => {
    return yup.object().shape({
      dealerId: yup.string().required(t('shared_dealers_overlay_selectMyDealer_validation_required')).nullable(),
    });
  }, [t]);

  const methods = useForm({
    resolver: yupResolver(setNewMainDealerValidationSchema),
  });

  const onSubmit = async (formValues: any) => {
    addDealerAsFavoriteOrMainMutation.mutate({ dealerId: formValues.dealerId, preferenceType: PreferenceType.MAIN });
    methods.reset();
  };

  /*********************************************
   *                 MODAL HANDLING
   *********************************************/
  const handleCloseMainDealerRemovalDialog = (params?: { id: string; shouldDeselectDealerAfterRemoval: boolean }) => {
    if (removeMainDealerModalState === 'STEP_2' && params?.id) {
      removeDealerPreferenceMutation.mutate({ dealerId: params.id });
      setRemoveMainDealerModalState?.('HIDDEN');
      setMainDealerToDelete?.(undefined);
      setSelectedDealer?.(undefined);
      if (params.shouldDeselectDealerAfterRemoval) {
        setSelectedDealer?.(undefined);
      }
    } else {
      setRemoveMainDealerModalState?.('HIDDEN');
    }
  };

  const handleConfirmRemoveMainDealer = () => {
    if (removeMainDealerModalState === 'STEP_1') {
      const favorites = queryClient
        .getQueryData<Dealer[]>(['DEALERS', 'FAVORITES'])
        ?.filter((dealer) => dealer.preference?.type === PreferenceType.FAVORITE);

      if (favorites && favorites.length > 0) {
        setRemoveMainDealerModalState?.('STEP_2');
      } else {
        if (!mainDealerToDelete) {
          setRemoveMainDealerModalState?.('HIDDEN');
          return;
        }

        removeDealerPreferenceMutation.mutate({ dealerId: mainDealerToDelete.id });
        setRemoveMainDealerModalState?.('HIDDEN');
        setMainDealerToDelete?.(undefined);
        if (mainDealerToDelete.shouldDeselectDealerAfterRemoval) {
          setSelectedDealer?.(undefined);
        }
      }
    }
  };

  const removeDealerPreference = (dealer: Dealer, shouldDeselectDealerAfterRemoval: boolean) => {
    if (dealer.preference?.type === PreferenceType.FAVORITE) {
      if (shouldDeselectDealerAfterRemoval) {
        setSelectedDealer?.(undefined);
      }
      removeDealerPreferenceMutation.mutate({ dealerId: dealer.id });
      if (shouldDeselectDealerAfterRemoval) {
        setSelectedDealer?.(undefined);
      }
    } else {
      setMainDealerToDelete?.({ id: dealer.id, shouldDeselectDealerAfterRemoval });
      setRemoveMainDealerModalState?.('STEP_1');
    }
  };

  const hasMainDealer = () => {
    return queryClient
      .getQueryData<Dealer[]>(queryKeys?.get('FAVORITES') || ['DEALERS', 'FAVORITES'])
      ?.some((dealer) => dealer.preference?.type === 'MAIN');
  };

  useEffect(() => {
    if (removeDealerPreferenceMutation.isSuccess || addDealerAsFavoriteOrMainMutation.isSuccess) {
      queryClient.invalidateQueries(['DEALERS']);
    }
  }, [removeDealerPreferenceMutation.isSuccess, addDealerAsFavoriteOrMainMutation.isSuccess]);

  const isLoadingRemove = (dealerId: string) =>
    removeDealerPreferenceMutation?.isLoading && removeDealerPreferenceMutation?.variables?.dealerId === dealerId;
  const isLoadingAdd = (dealerId: string) =>
    addDealerAsFavoriteOrMainMutation?.isLoading && addDealerAsFavoriteOrMainMutation?.variables?.dealerId === dealerId;

  return {
    removeDealerPreference,
    isLoadingRemove,
    isLoadingAdd,

    handleCloseMainDealerRemovalDialog,
    handleConfirmRemoveMainDealer,

    removeMainDealerModalState,
    mainDealerToDelete,

    addDealerAsFavorite: (dealerId: string) =>
      addDealerAsFavoriteOrMainMutation.mutate({ dealerId, preferenceType: PreferenceType.FAVORITE }),
    addDealerAsMain: (dealerId: string) =>
      addDealerAsFavoriteOrMainMutation.mutate({
        dealerId,
        preferenceType: PreferenceType.MAIN,
      }),

    hasMainDealer,

    formProps: {
      methods,
      onSubmit: methods.handleSubmit(onSubmit),
      isLoading: isLoadingAdd(methods.watch('dealerId')),
    },
  };
};

export { useDealerPreferences };
